import {
  describeSchema,
  type IArrayValidationDescription,
  type IValidationDescription,
} from '@framework/validation/describe/describeSchema.ts';
import { IsStringDateMetaKey } from '@framework/validation/methods/stringDate.ts';
import { type ISchema } from 'yup';
import { EngineInternalFieldSuffix } from '../../schema/expressions/EngineInternalFieldSuffix.ts';
import { type IEngineSectionStatus } from '../status/SectionStatusSchema.ts';
import { serializeFormSectionsStatus } from '../status/serializeFormSectionsStatus.ts';

export function serializeFormValues(
  schema: ISchema<any>,
  formValues: Record<string, any>,
) {
  const description = describeSchema(schema, { value: formValues });
  return serializeFormSchema(
    undefined,
    description,
    true,
  ) as ISerializedRootFormValue;
}

export type ISerializedFormValue =
  | ISerializedObjectFormValue
  | ISerializedArrayFormValue
  | ISerializedPrimitiveFormValue
  | ISerializedRootFormValue
  | null;

export interface ISerializedRootFormValue {
  name?: string; // Rimuovere
  version?: string;
  values?: ISerializedFormValue[];
  sections?: Record<string, IEngineSectionStatus>;
}

export interface ISerializedObjectFormValue {
  name?: string;
  type: 'object';
  values: ISerializedFormValue[];
}

export interface ISerializedArrayFormValue {
  name?: string;
  type: 'array';
  items: ISerializedFormValue[];
}

export interface ISerializedPrimitiveFormValue {
  name?: string;
  type: 'string' | 'number' | 'boolean' | 'date';
  value: any;
}

function serializeFormSchema(
  name: string | undefined,
  schema: IValidationDescription,
  isRoot = false,
): ISerializedFormValue {
  switch (schema.type) {
    case 'object': {
      const values = Object.entries(schema.schemas)
        .filter(
          // Ignora i campi interni (finiscono con `@internal`) oppure i campi
          // che non hanno uno schema associato
          ([k, v]) => v !== undefined && !k.endsWith(EngineInternalFieldSuffix),
        )
        .map(([key, value]) => {
          return serializeFormSchema(key, value);
        })
        .filter(v => v != null);

      if (isRoot) {
        return {
          version: 'v1',
          values,
          ...serializeFormSectionsStatus(schema),
        };
      }

      return {
        name,
        type: 'object',
        values,
      };
    }

    case 'array': {
      return serializeArraySchema(name, schema);
    }

    case 'primitive': {
      if (schema.value === undefined) return null;
      return {
        name,
        type: schema.desc?.meta?.[IsStringDateMetaKey]
          ? 'date'
          : ((schema.desc?.type as ISerializedPrimitiveFormValue['type']) ??
            'string'),
        value: schema.value,
      };
    }
  }
}

function serializeArraySchema(
  name: string | undefined,
  schema: IArrayValidationDescription,
): ISerializedFormValue {
  // Gli array disabled vengono hanno `.strip()`, quindi il cast (es. `syncComputedValues`)
  // li rimuove per non lasciare valori nel form state.
  // In serializzazione però il contratto API resta "array vuoto".
  const forceEmptyArray = isDisabledMissingArray(schema);

  if (isPrimitiveArraySchema(schema)) {
    const value = forceEmptyArray ? [] : schema.value;
    if (value === undefined) return null;

    return serializePrimitiveArraySchema(name, schema, value);
  }

  return {
    name,
    type: 'array',
    items: forceEmptyArray ? [] : serializeObjectArrayItems(schema),
  };
}

function isDisabledMissingArray(schema: IArrayValidationDescription) {
  return (
    schema.value === undefined && schema.desc.spec?.meta?.enabled === false
  );
}

function isPrimitiveArraySchema(schema: IArrayValidationDescription) {
  return schema.innerType?.type !== 'object';
}

/**
 * Gli array primitivi (es. multi-select) non usano `{ type: 'array', items }`:
 * li serializziamo come valore primitivo con `value: T[]`.
 */
function serializePrimitiveArraySchema(
  name: string | undefined,
  schema: IArrayValidationDescription,
  value: any[],
): ISerializedPrimitiveFormValue {
  return {
    name: name ?? '',
    type:
      (schema.innerType?.type as ISerializedPrimitiveFormValue['type']) ??
      'string',
    value,
  };
}

function serializeObjectArrayItems(schema: IArrayValidationDescription) {
  return schema.schemas
    .map(value => serializeFormSchema('', value))
    .filter(v => v !== undefined);
}
